import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { tenantsAPI } from '../services/api';
import { TenantContext } from './TenantContext';

const TENANT_STORAGE_KEY = 'activeTenantId';

const readStoredTenantId = () => {
  try {
    return localStorage.getItem(TENANT_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStoredTenantId = (tenantId) => {
  try {
    if (tenantId) {
      localStorage.setItem(TENANT_STORAGE_KEY, tenantId);
    } else {
      localStorage.removeItem(TENANT_STORAGE_KEY);
    }
  } catch {
    // localStorage unavailable — fall through silently.
  }
};

/**
 * Tenant state for the authenticated SPA. Depends on AuthProvider being higher
 * up the tree. The active tenant id is mirrored to localStorage so
 * httpClient can attach `X-Tenant-Id` on every request without a React render.
 */
export const TenantProvider = ({ children }) => {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [activeTenant, setActiveTenantState] = useState(null);
  const [loading, setLoading] = useState(true);

  const setActiveTenant = useCallback((tenant) => {
    setActiveTenantState(tenant);
    writeStoredTenantId(tenant?.id || null);
  }, []);

  const refreshTenants = useCallback(async () => {
    try {
      const data = await tenantsAPI.listMine();
      const memberships = Array.isArray(data?.tenants) ? data.tenants : [];
      setTenants(memberships);

      const preferredId = readStoredTenantId();
      const preferred =
        (preferredId && memberships.find((t) => t.id === preferredId)) ||
        data?.activeTenant ||
        memberships.find((t) => t.isDefault) ||
        memberships[0] ||
        null;
      setActiveTenant(preferred);
    } catch {
      setTenants([]);
      setActiveTenant(null);
    }
  }, [setActiveTenant]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!user) {
        setTenants([]);
        setActiveTenant(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      await refreshTenants();
      if (!cancelled) setLoading(false);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [user, refreshTenants, setActiveTenant]);

  const value = useMemo(
    () => ({
      tenants,
      activeTenant,
      loading,
      setActiveTenant,
      refreshTenants,
    }),
    [tenants, activeTenant, loading, setActiveTenant, refreshTenants]
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

export const TENANT_STORAGE_KEY_EXPORT = TENANT_STORAGE_KEY;
